import {useRequiredConfig} from '@domain/shifthandover'
import {filterSxStyles} from '@hconnect/common/styles'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {FilterList as FilterIcon} from '@mui/icons-material'
import {Badge, Button, Box, useMediaQuery, Theme} from '@mui/material'
import {sortingFilterParams} from '@widgets/shift-event-filters'
import {isEqual, omit} from 'lodash'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {PerformanceFilter} from './PerformanceFilter'
import type {PerformanceFilterOptions} from './PerformanceFilter'

type Props = {
  isCollapsible: boolean
  activeSettings: PerformanceFilterOptions
  onChange: (next: PerformanceFilterOptions) => void
  openMobileFilter: (state: boolean) => void
  isMobileFilterOpen: boolean
  plantId: string
}

export const PerformanceFilterBar: React.FC<Props> = ({
  isCollapsible,
  activeSettings,
  onChange,
  openMobileFilter,
  isMobileFilterOpen,
  plantId
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const {shifts} = useRequiredConfig(plantId)
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const [settings, setSettings] = useState<PerformanceFilterOptions>(activeSettings)

  useEffect(() => {
    setSettings(activeSettings)
  }, [activeSettings])

  if (isCollapsible && !isMobileFilterOpen) {
    const filters = omit(settings, sortingFilterParams)
    const totalFilters: number = Object.keys(filters).reduce((acc, curr) => {
      if (Array.isArray(settings[curr])) {
        acc += settings[curr].length
      } else if (settings[curr]) {
        acc += 1
      }
      return acc
    }, 0)
    return (
      <Box data-test-id={'performance-filter-bar'} sx={filterSxStyles.filterBar}>
        <Box sx={filterSxStyles.filterButton}>
          <Badge
            badgeContent={totalFilters}
            color="primary"
            data-test-id="performance-filters-badge"
            overlap="circular"
          >
            <Button
              variant="text"
              startIcon={!isMobile && <FilterIcon />}
              sx={filterSxStyles.filterSearchButton}
              onClick={() => {
                openMobileFilter(true)
              }}
              data-test-id="performance-filter-bar-toggle-button"
              id="performance-filter-bar-toggle-button"
            >
              {isMobile ? <FilterIcon /> : t('shiftEvent.action.filter')}
            </Button>
          </Badge>
        </Box>
      </Box>
    )
  }

  const update = (next: PerformanceFilterOptions) => {
    if (isEqual(settings, next)) {
      return
    }

    if (isCollapsible) {
      setSettings(next)
    } else {
      onChange(next)
    }
  }

  return (
    <Box
      data-test-id="performance-filter-bar-box"
      sx={isMobileFilterOpen ? filterSxStyles.filterLayover : filterSxStyles.filterBar}
      display={'flex'}
      flexDirection={'column'}
    >
      {isCollapsible && (
        <Box sx={filterSxStyles.filterButton} mr={1} textAlign={'right'}>
          <Button
            data-test-id="performance-filter-bar-cancel-button"
            id="performance-filter-bar-cancel-button"
            variant={'contained'}
            onClick={() => {
              openMobileFilter(false)
              setSettings(activeSettings)
            }}
          >
            {t('action.cancel')}
          </Button>
        </Box>
      )}
      <PerformanceFilter
        onChange={update}
        settings={settings}
        isCollapsible={isCollapsible}
        applyFilter={(filterSettings) => {
          openMobileFilter(false)
          onChange(filterSettings)
        }}
        shifts={shifts}
        timezone={timezone}
        plantNow={getPlantNow()}
      />
    </Box>
  )
}
